import {forwardRef} from 'react'
import {Radio as RadioPrimitive} from '@base-ui/react/radio'
import {RadioGroup as BaseRadioGroup} from '@base-ui/react/radio-group'
import {InputHelpText} from '@components/ui/input-help-text'
import {cn} from '@utilities/helpers'

/**
 * @component
 *
 * @example
 * // Basic usage
 * <RadioGroup defaultValue="option1" aria-label="Select an option">
 *   <Radio value="option1" id="option1" label="Option 1" />
 *   <Radio value="option2" id="option2" label="Option 2" />
 * </RadioGroup>
 *
 * @example
 * // With form integration
 * <Controller
 *   name="preference"
 *   control={control}
 *   render={({field: {onChange, value}}) => (
 *     <RadioGroup
 *       onValueChange={onChange}
 *       value={value}
 *       aria-label="Select preference"
 *     >
 *       <Radio value="email" id="email" label="Email" />
 *       <Radio value="sms" id="sms" label="SMS" />
 *     </RadioGroup>
 *   )}
 * />
 *
 * @param props - Component props
 * @param props.className - Additional CSS classes to merge with default styles
 * @param props.props - All other props are passed to the underlying Base UI RadioGroup
 */
function RadioGroup({className, ...props}: BaseRadioGroup.Props) {
    return <BaseRadioGroup className={cn('grid gap-2', className)} data-slot="radio-group" {...props} />
}

/**
 * RadioGroupItem - individual radio group item
 *
 * Represents a single selectable item within RadioGroup.
 * When selected, gets a `primary-50` background with a `primary-600` border and dot;
 * unselected shows a white background with a `neutral-300` border.
 *
 * @component
 *
 * @example
 * // Basic usage (must be inside RadioGroup)
 * <Radio value="option1" id="option1" label="Option 1" />
 *
 * @example
 * // Disabled state
 * <Radio value="option3" id="option3" label="Option 3" disabled />
 *
 * @param props - Component props
 * @param props.className - Additional CSS classes to merge with default styles
 * @param props.props - All other props are passed to the underlying Base UI Radio.Root
 */

interface RadioGroupItemProps extends RadioPrimitive.Root.Props {
    id: string
    label: string
    labelPosition?: 'left' | 'right'
    errorMessage?: string
    helpText?: string
    className?: string
    rootClassName?: string
}
const Radio = forwardRef<HTMLElement, RadioGroupItemProps>(function Radio(
    {className, rootClassName, labelPosition = 'right', helpText, label, errorMessage, id, ...props},
    ref
) {
    return (
        <div className={cn('flex flex-col gap-2', rootClassName)}>
            <div className={cn('flex gap-3', labelPosition === 'right' ? 'flex-row' : 'flex-row-reverse text-end')}>
                <RadioPrimitive.Root
                    ref={ref}
                    className={cn(
                        'outline-none group flex aspect-square size-5 flex-none cursor-pointer appearance-none items-center justify-center rounded-full border border-neutral-300 bg-white p-1 transition',
                        'data-checked:border-primary-600 data-checked:bg-primary-50',
                        'data-disabled:cursor-not-allowed data-disabled:border-neutral-300 data-disabled:bg-neutral-100',
                        'hover:not-data-disabled:border-primary-300 hover:not-data-disabled:shadow-[var(--shadow-xs),0px_0px_0px_2px_var(--color-primary-100)]',
                        labelPosition === 'left' ? 'order-2' : 'order-0',
                        className
                    )}
                    data-slot="radio-group-item"
                    id={id}
                    {...props}
                >
                    <RadioPrimitive.Indicator
                        className="relative flex items-center justify-center"
                        data-slot="radio-group-indicator"
                    >
                        <div className="size-2.5 rounded-full bg-primary-600 group-data-disabled:bg-neutral-300" />
                    </RadioPrimitive.Indicator>
                </RadioPrimitive.Root>

                <label className="wrap-anywhere cursor-pointer whitespace-normal text-neutral-900 text-md" htmlFor={id}>
                    {label}
                </label>
            </div>
            <InputHelpText error={errorMessage} helpText={helpText} />
        </div>
    )
})

Radio.displayName = 'Radio'

export {RadioGroup, Radio}
